// Visar hur mycket som är kvar av gratisperioden och dess kvoter.
import React from "react";
import { Clock, Sparkles, Link2, FileDown, ArrowUpRight } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Link } from "react-router-dom";
import { useFreeTier } from "@/hooks/useFreeTier";

const Quota: React.FC<{ icon: React.ReactNode; label: string; used: number; limit: number }> = ({
  icon,
  label,
  used,
  limit,
}) => {
  const pct = limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : 0;
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between gap-2 text-xs">
        <span className="flex items-center gap-1.5 text-muted-foreground">
          {icon}
          {label}
        </span>
        <span className="tabular-nums font-medium">
          {used}/{limit}
        </span>
      </div>
      <Progress value={pct} className="h-1.5" />
    </div>
  );
};

export const FreeTierBanner: React.FC<{ compact?: boolean }> = ({ compact = false }) => {
  const { state, loading, isFree } = useFreeTier();

  if (loading || !isFree || !state) return null;

  return (
    <Card className="border-primary/30 bg-primary/5">
      <CardContent className="space-y-4 p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="space-y-1">
            <div className="flex flex-wrap items-center gap-2">
              <Badge className="gap-1">
                <Sparkles className="h-3 w-3" />
                Provperiod – hela systemet öppet
              </Badge>
              {state.expired ? (
                <Badge variant="destructive">Provperioden är slut</Badge>
              ) : (
                <Badge variant="outline" className="gap-1">
                  <Clock className="h-3 w-3" />
                  {state.days_left} dagar kvar
                </Badge>
              )}
            </div>
            <p className="text-sm text-muted-foreground">
              {state.expired
                ? "Ditt projekt ligger kvar i läsläge. Uppgradera för att fortsätta redigera – allt du fyllt i följer med."
                : "Alla funktioner är öppna: AI-stöd, externa länkar, alla dokument och Word-export."}
            </p>
          </div>
          <Link to="/hjalp">
            <Button size="sm" className="gap-1">
              Uppgradera
              <ArrowUpRight className="h-4 w-4" />
            </Button>
          </Link>
        </div>

        {!compact && (
          <div className="grid gap-3 sm:grid-cols-3">
            <Quota
              icon={<Sparkles className="h-3.5 w-3.5" />}
              label="AI-anrop"
              used={state.ai_used}
              limit={state.ai_limit}
            />
            <Quota
              icon={<Link2 className="h-3.5 w-3.5" />}
              label="Externa länkar"
              used={state.links_used}
              limit={state.links_limit}
            />
            <Quota
              icon={<FileDown className="h-3.5 w-3.5" />}
              label="Word-exporter"
              used={state.exports_used}
              limit={state.exports_limit}
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default FreeTierBanner;
